import { useId, useRef, type ReactNode, type SelectHTMLAttributes, type InputHTMLAttributes } from "react";
import { centavosDe, formatarCentavos } from "../formatos";
import { voltarAoTopo } from "./Dados";
import { BUSCA_A_PARTIR_DE, ListaComBusca } from "./ListaComBusca";
import { Paginacao, paginar, type PaginacaoDaLista } from "./Paginacao";

/**
 * Planilha (spec 054): tabela de celulas editaveis, pra acertar muitos registros de uma vez sem abrir um dialogo por
 * linha. Rola dentro da propria caixa, com cabecalho e a coluna `fixa` parados na rolagem (a pagina nao rola de lado).
 * Quem usa guarda o rascunho de cada linha e diz o `estado` dela: "mudou" (tem o que salvar) ou "erro".
 * No celular continua planilha (rola), porque o trabalho e comparar colunas lado a lado.
 */
export type ColunaDaPlanilha<L> = { chave: string; rotulo: string; fixa?: boolean; alinhar?: "direita"; celula: (l: L) => ReactNode };
export type EstadoDaLinha = "mudou" | "erro" | undefined;

export function Planilha<L>({ rotulo, colunas, linhas, chave, estado, vazio, paginacao }: { rotulo: string; colunas: ColunaDaPlanilha<L>[]; linhas: L[]; chave: (l: L) => string; estado?: (l: L) => EstadoDaLinha; vazio?: ReactNode; /** De 20 em 20 (spec 058): quem usa guarda o rascunho por linha, entao trocar de pagina nao perde nada. */ paginacao?: PaginacaoDaLista }) {
  const topo = useRef<HTMLDivElement>(null);
  if (!linhas.length && vazio) return <>{vazio}</>;
  const total = paginacao ? (paginacao.total ?? linhas.length) : linhas.length;
  const visiveis = paginacao && paginacao.total == null ? paginar(linhas, paginacao.pagina) : linhas;
  return (
    <>
    <div className="mtc-planilha" role="region" aria-label={rotulo} tabIndex={0} ref={topo}>
      <table className="mtc-planilha__tabela">
        <thead>
          <tr>{colunas.map((c) => <th key={c.chave} scope="col" data-fixa={c.fixa || undefined} data-alinhar={c.alinhar}>{c.rotulo}</th>)}</tr>
        </thead>
        <tbody>
          {visiveis.map((l) => (
            <tr key={chave(l)} data-estado={estado?.(l)}>
              {colunas.map((c) => <td key={c.chave} data-fixa={c.fixa || undefined} data-alinhar={c.alinhar}>{c.celula(l)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {paginacao && <Paginacao pagina={paginacao.pagina} total={total} rotulo={paginacao.rotulo} aoMudar={(n) => { paginacao.aoMudar(n); if (topo.current) topo.current.scrollTop = 0; voltarAoTopo(topo.current); }} />}
    </>
  );
}

type Comum = { rotulo: string; mudou?: boolean };

/** Celula de texto ou data. `rotulo` vira o nome acessivel (a planilha nao tem rotulo em cada celula). */
export function EntradaDaPlanilha({ rotulo, mudou, valor, aoMudar, mono, className, ...p }: Comum & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & { valor: string; aoMudar: (v: string) => void; mono?: boolean }) {
  const classes = ["mtc-planilha__entrada", mono && "mtc-planilha__entrada--mono", className].filter(Boolean).join(" ");
  return <input className={classes} aria-label={rotulo} data-mudou={mudou || undefined} value={valor} onChange={(e) => aoMudar(e.target.value)} {...p} />;
}

/** Celula de dinheiro em centavos: digita "1.234,56" ou so numeros. Zero fica vazio. */
export function DinheiroDaPlanilha({ rotulo, mudou, centavos, aoMudar, disabled }: Comum & { centavos: number | null; aoMudar: (c: number | null) => void; disabled?: boolean }) {
  return <EntradaDaPlanilha rotulo={rotulo} mudou={mudou} mono className="mtc-planilha__entrada--dinheiro" inputMode="numeric" placeholder="0,00" disabled={disabled} valor={centavos ? formatarCentavos(centavos) : ""} aoMudar={(v) => aoMudar(centavosDe(v) || null)} />;
}

/** Celula de escolha (select nativo). */
export function SelecaoDaPlanilha<T extends string>({ rotulo, mudou, valor, aoMudar, opcoes, ...p }: Comum & Omit<SelectHTMLAttributes<HTMLSelectElement>, "value" | "onChange"> & { valor: T | ""; aoMudar: (v: T) => void; opcoes: { valor: T | ""; rotulo: string }[] }) {
  const id = useId();
  // Lista comprida (planos do catalogo, mesas, motivos) ganha busca dentro dela, como o CampoSelecao (spec 059, bloco 2).
  if (opcoes.length >= BUSCA_A_PARTIR_DE) {
    return <ListaComBusca<string> id={id} rotulo={rotulo} rotuloOculto valor={valor} aoMudar={(v) => aoMudar(v as T)} opcoes={opcoes} disabled={p.disabled} mudou={mudou} classe="mtc-planilha__entrada" />;
  }
  return (
    <select className="mtc-planilha__entrada" aria-label={rotulo} data-mudou={mudou || undefined} value={valor} onChange={(e) => aoMudar(e.target.value as T)} {...p}>
      {opcoes.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
    </select>
  );
}
